import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
} from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { isAfter, isBefore } from 'date-fns';
import { useCart } from '~/hooks/cart';
import { useSettings } from '~/hooks/settings';
import { useAppAuth } from '~/hooks/appAuth';
import { useCategories } from '~/hooks/categories';
import { useAnalytics } from '~/hooks/analytics';

import App from '~/layouts/App';
import getCurrencyFormatted from '~/utils/getCurrencyFormatted';
import ICartProduct from '~/interfaces/ICartProduct';
import IDistance from '~/interfaces/IDistance';
import Login from '~/pages/app/Login';
import { DB } from '~/services/firebase';
import { useMenus } from '~/hooks/menus';
import Address from './Address';
import { Coupon } from './components/Coupon';
import IClient from '~/interfaces/IClient';
import IComplement from '~/interfaces/IComplement';

const { REACT_APP_SIMPLE_ADDRESS, REACT_APP_SIMPLE_ADDRESS_WITHOUT_LOCALITY } =
  process.env;

const Cart: React.FC = () => {
  const { logAddToCart, logEvent } = useAnalytics();
  const {
    products,
    plus,
    minus,
    deliveryMethod,
    changeDeliveryMethod,
    address,
    updateDistances,
    addressFilled,
    coupon,
    fidelitDiscount,
    setFidelitDiscount,
  } = useCart();
  const { company, table } = useSettings();
  const { user } = useAppAuth();
  const { complements, categories } = useCategories();

  const { menus, loadMenus } = useMenus();

  const [openAddress, setOpenAddress] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [distances, setDistances] = useState<IDistance[]>([]);
  const [isLoadDistance, setIsLoadingDistance] = useState(false);

  const menuEnableButton = useMemo(() => {
    let result = menus.length === 0;

    if (menus.length > 0) {
      const currentDate = new Date();

      menus.forEach(item => {
        const [itemInitHour, itemInitMinutes] = item.initHour.split(':');
        const [itemEndHour, itemEndMinutes] = item.endHour.split(':');
        const itemInitDate = new Date();
        itemInitDate.setHours(
          parseInt(itemInitHour),
          parseInt(itemInitMinutes),
        );
        const itemEndDate = new Date();
        itemEndDate.setHours(parseInt(itemEndHour), parseInt(itemEndMinutes));

        if (
          item.status &&
          isAfter(currentDate, itemInitDate) &&
          isBefore(currentDate, itemEndDate) &&
          item.weekDays.includes(`${currentDate.getDay()}`)
        ) {
          result = true;
        }
      });
    }

    return result;
  }, [menus]);

  useEffect(() => {
    async function init() {
      loadMenus(company.uid);
    }

    if (company.uid) {
      init();
    }
  }, [loadMenus, company]);

  useEffect(() => {
    async function loadDistances() {
      const q = query(
        collection(DB, 'distances'),
        where('company_id', '==', company.uid),
      );
      const querySnapshot = await getDocs(q);

      const items: IDistance[] = [];

      querySnapshot.docs.forEach(item => {
        const parseItem = {
          ...item.data(),
          uid: item.id,
        } as IDistance;

        items.push(parseItem);
      });

      items.sort((a, b) => (a.from > b.from ? 1 : b.from > a.from ? -1 : 0));

      setDistances(items);
      updateDistances(items);
      setIsLoadingDistance(true);
    }

    if (company && company.uid && distances.length === 0 && !isLoadDistance) {
      loadDistances();
    }
  }, [company, distances, updateDistances, isLoadDistance]);

  const parseProducts = useMemo(() => {
    const tmp: ICartProduct[] = [];

    products.forEach(item => {
      let total = 0;

      // Check if any complement belongs to a pizza-type group
      const hasPizzaTypeComplement = item.complements.some(element => {
        const complementGroup = complements.find(group => group.uid === element.group);
        return complementGroup?.pizzaType;
      });

      if (hasPizzaTypeComplement) {
        // For pizza-type groups, use the highest value among selected items
        item.complements.forEach(element => {
          const complementGroup = complements.find(group => group.uid === element.group);
          if (complementGroup?.pizzaType) {
            const itemValue = parseFloat(element.item.value);
            if (itemValue > total) total = itemValue;
          } else {
            // For non-pizza groups, add normally
            total += element.quantity * parseFloat(element.item.value);
          }
        });
      } else {
        // Normal calculation for non-pizza products
        item.complements.forEach(element => {
          total += element.quantity * parseFloat(element.item.value);
        });
      }

      tmp.push({
        ...item,
        value: `${parseFloat(item.value) + total}`,
      });
    });

    return tmp;
  }, [products, complements]);

  const shippingCost = useMemo(() => {
    if (!company.uid || deliveryMethod === '0' || company.free_delivery === '1')
      return 0;

    if (distances.length === 0) return parseFloat(company.shipping_cost);

    if (REACT_APP_SIMPLE_ADDRESS) {
      const findDistance = distances.find(
        find => find.district === address.neighbourhood,
      );
      if (findDistance) {
        return findDistance?.value;
      }
    }

    if (address.distance) {
      const distanceKm = address.distance?.value / 1000;

      const findDistance = distances.find(find => {
        if (
          find.district &&
          `${find.district}`.toLowerCase() ===
            `${address.neighbourhood}`.toLowerCase()
        )
          return find;

        if (distanceKm > find.from && distanceKm <= find.to) return find;

        if (distanceKm === 0 && distances.length > 0) return distances[0];

        return null;
      });

      if (findDistance && findDistance.value) return findDistance.value;
    }

    return parseFloat(company.shipping_cost);
  }, [company, deliveryMethod, address, distances]);

  const shippingCostDescription = useMemo(() => {
    if (deliveryMethod !== '1' || company.free_delivery === '1') {
      return 'Grátis';
    }

    return getCurrencyFormatted(shippingCost);
  }, [deliveryMethod, shippingCost, company]);

  const subtotal = useMemo(() => {
    let total = 0;

    parseProducts.forEach(item => {
      total += parseFloat(`${item.quantity}`) * parseFloat(item.value);
    });

    return total;
  }, [parseProducts]);

  const couponDiscount = useMemo(() => {
    if (!coupon.uid) return 0;

    return coupon.type === 0
      ? parseFloat(((subtotal * coupon.value) / 100).toFixed(2))
      : coupon.value;
  }, [coupon, subtotal]);

  const total = useMemo(() => {
    if (!company.uid) return 0;

    let calcTotal = subtotal - couponDiscount - fidelitDiscount;

    if (!table.uid) {
      calcTotal += shippingCost;
    }

    return calcTotal;
  }, [subtotal, company, shippingCost, couponDiscount, fidelitDiscount, table]);

  const addressIsValid = useMemo(() => {
    if (REACT_APP_SIMPLE_ADDRESS && REACT_APP_SIMPLE_ADDRESS_WITHOUT_LOCALITY) {
      return address.road && address.number;
    }

    return REACT_APP_SIMPLE_ADDRESS
      ? address.road && address.number && address.locality
      : address.city &&
          address.neighbourhood &&
          address.number &&
          address.state &&
          address.road;
  }, [address]);

  const fidelitAlert = useMemo(() => {
    if (company.fidelit && company.fidelit_min_value) {
      if (subtotal < company.fidelit_min_value) {
        return {
          message: `Atenção! Essa compra não contará como fidelidade, pois o valor é inferior ao mínimo de ${getCurrencyFormatted(
            company.fidelit_min_value,
          )}.`,
          value: true,
        };
      }
    }

    return {
      message: '',
      value: false,
    };
  }, [company, subtotal]);

  const handleClickContinue = useCallback(() => {
    if (!user || !user.uid) {
      setShowLogin(true);
      return;
    }

    if (!addressFilled && deliveryMethod === '1') {
      setOpenAddress(true);

      return;
    }

    document.getElementById('checkout')?.click();

    // history.push(`/${company.app_domain}/checkout`);
  }, [deliveryMethod, addressFilled, user]);

  useEffect(() => {
    if (table.uid) changeDeliveryMethod('2');
    else changeDeliveryMethod('1');
  }, [table, changeDeliveryMethod]);

  useEffect(() => {
    async function loadUserFidelitPoints() {
      if (!user || !user.uid) return;

      const docClient = await getDoc(doc(DB, 'clients', user.uid));
      const client = docClient.data() as IClient;

      if (
        company.fidelit &&
        company.fidelit_quantity &&
        !fidelitAlert.value &&
        client.fidelit_order_quantity
      ) {
        if (client.fidelit_order_quantity >= company.fidelit_quantity) {
          setFidelitDiscount(client.fidelit_balance || 0);
        }
      }
    }

    loadUserFidelitPoints();
  }, [fidelitDiscount, user, company, setFidelitDiscount, fidelitAlert]);

  return (
    <App loading={false}>
      {showLogin && <Login onClose={() => setShowLogin(false)} />}

      {parseProducts.length === 0 && (
        <div className="row mb-3 mt-5">
          <div className="col align-self-center text-center">
            <h5 className="mb-0">Sua sacola de compras està vazia.</h5>
            <div className="row mb-3 mt-4">
              <div className="col align-self-center d-grid centered">
                <Link
                  to={`/${company.app_domain}`}
                  className="btn btn-danger btn-lg shadow-sm text-white"
                  style={{
                    background: company.app_color,
                    width: 200,
                    border: 0,
                  }}
                >
                  Ir às compras
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {parseProducts.length > 0 && (
        <div className="row mb-3 mt-5">
          <div className="col align-self-center">
            <h5 className="mb-0">{`${parseProducts.length} produtos na sacola`}</h5>
          </div>
        </div>
      )}

      <div className="row mb-2">
        {parseProducts.map((item, index) => (
          <div className="col-12 col-md-6 col-lg-4" key={item.uid}>
            <div className="card shadow-sm product mb-3">
              <div className="card-body">
                <div className="row">
                  <div className="col-auto">
                    <figure
                      className="text-center avatar-90 avatar"
                      style={{ background: `${company.app_color}60` }}
                    >
                      <img src={item.image || company.image} alt="produto" style={{ height: '100%', objectFit: 'cover', marginTop: '-2px' }} />
                    </figure>
                  </div>
                  <div className="col ps-0">
                    <p className="mb-0">
                      <small className="text-opac">{item.category.name}</small>
                      {/* <small className="float-end">
                        <i className="bi bi-star-fill text-warning" />{' '}
                        <span className="text-opac">4.0 (165 Reviews)</span>
                      </small> */}
                    </p>
                    <h6
                      className="text-color-theme"
                      style={{ color: company.app_color }}
                    >
                      {item.name}
                    </h6>
                    <div className="row">
                      <div className="col">
                        <p className="mb-0">
                          {getCurrencyFormatted(parseFloat(item.value))}
                          {/* <small className="text-opac">{getCurrencyFormatted(parseFloat(item.value) * item.quantity)}</small> */}

                          {item.measure === 'KG' && (
                            <>
                              <br />
                              <small className="text-opac">por 1 kg</small>
                            </>
                          )}
                        </p>
                      </div>
                      {item.measure === 'KG' ? (
                        <div className="col-auto">
                          <div className="counter-number">
                            <button
                              type="button"
                              className="btn btn-sm avatar avatar-30 p-0 rounded-circle"
                              onClick={() => minus(index)}
                              style={{ background: `${company.app_color}60` }}
                            >
                              <i
                                className="bi bi-dash size-22"
                                style={{ color: company.app_color }}
                              />
                            </button>
                            <input
                              type="number"
                              value={item.quantity.toFixed(3)}
                              onChange={() => {}}
                            />
                            <button
                              type="button"
                              className="btn btn-sm avatar avatar-30 p-0 rounded-circle"
                              onClick={() => plus(index)}
                              style={{ background: `${company.app_color}60` }}
                            >
                              <i
                                className="bi bi-plus size-22"
                                style={{ color: company.app_color }}
                              />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="col-auto">
                          <div className="counter-number">
                            <button
                              type="button"
                              className="btn btn-sm avatar avatar-30 p-0 rounded-circle"
                              onClick={() => minus(index)}
                              style={{ background: `${company.app_color}60` }}
                            >
                              <i
                                className="bi bi-dash size-22"
                                style={{ color: company.app_color }}
                              />
                            </button>
                            <span>{item.quantity}</span>
                            <button
                              type="button"
                              className="btn btn-sm avatar avatar-30 p-0 rounded-circle"
                              onClick={() => plus(index)}
                              style={{ background: `${company.app_color}60` }}
                            >
                              <i
                                className="bi bi-plus size-22"
                                style={{ color: company.app_color }}
                              />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    <p>
                      {item.complements.map(comp => (
                        <Fragment key={comp.item.uid}>
                          <small className="text-opac">{`${comp.quantity}x - ${comp.item.name}`}</small>
                          <br />
                        </Fragment>
                      ))}
                      {item.observations && (
                        <small className="text-opac">
                          {`Obs: ${item.observations}`}
                        </small>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {products.length > 0 && (
        <form>
          <div className="row mb-3">
            <div className="col align-self-center">
              <h5 className="mb-0">Resumo</h5>
            </div>
          </div>
          <div className="row mb-2">
            {fidelitAlert.value && (
              <div className="alert alert-warning alert-icon alert-dismissible">
                <em className="icon ni ni-alert-circle" />
                {fidelitAlert.message}
              </div>
            )}

            <div
              className="col"
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <p>Forma de entrega</p>
            </div>
            <div className="col-auto pr-0">
              <select
                className="form-select"
                id="select"
                style={{ opacity: 1, textAlign: 'right' }}
                value={deliveryMethod}
                onChange={event => changeDeliveryMethod(event.target.value)}
              >
                {table && table.uid && <option value="2">Mesa</option>}
                <option value="1">Delivery</option>
                <option value="0">Retirar no local</option>
              </select>
            </div>
          </div>
          {deliveryMethod === '2' && (
            <div className="row mb-3">
              <div className="col">
                <p>Mesa</p>
              </div>
              <div className="col-auto">{table.name}</div>
            </div>
          )}
          {deliveryMethod === '1' && (
            <div className="row mb-2">
              <div
                className="col"
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <p>Endereço de entrega</p>
              </div>
              <div className="col-auto pr-0">
                <Link
                  to="#"
                  className="btn btn-light text-capitalize"
                  style={{ fontSize: 12 }}
                  onClick={event => {
                    event.preventDefault();
                    setOpenAddress(true);
                  }}
                >
                  {addressIsValid ? 'Alterar Endereço' : 'Informar Endereço'}
                </Link>
              </div>
            </div>
          )}

          <Coupon
            orderValue={subtotal}
            onOpenAuthModal={() => setShowLogin(true)}
          />

          {deliveryMethod === '1' && shippingCost >= 0 && (
            <div className="row mb-2">
              <div className="col">
                <p>Taxa de entrega</p>
              </div>
              <div className="col-auto">{shippingCostDescription}</div>
            </div>
          )}

          <div className="row mb-3">
            <div className="col">
              <p>Subtotal</p>
            </div>
            <div className="col-auto">{getCurrencyFormatted(subtotal)}</div>
          </div>

          {fidelitDiscount > 0 && (
            <div className="row mt-2">
              <div className="col">
                <p>Desconto Fidelidade</p>
              </div>
              <div className="col-auto">
                {`- ${getCurrencyFormatted(fidelitDiscount)}`}
              </div>
            </div>
          )}

          <div className="row fw-bold mb-4 mt-3">
            <div className="mb-3 col-12">
              <div className="dashed-line" />
            </div>
            <div className="col">
              <p>Total</p>
            </div>
            <div className="col-auto">{getCurrencyFormatted(total)}</div>
          </div>
          {/* Button */}
          <div className="row mb-3">
            <div className="col align-self-center d-grid">
              {/* {!addressIsValid && deliveryMethod === '1' && (
                <p style={{ fontSize: 10 }} className="mb-1">
                  * É necessário informar o endereço de entrega.
                </p>
              )} */}
              {company.opened && menuEnableButton && (
                <button
                  type="button"
                  className="btn btn-default btn-lg shadow-sm"
                  onClick={handleClickContinue}
                  // disabled={!addressIsValid && deliveryMethod === '1'}
                >
                  Continuar
                </button>
              )}
              {(!company.opened || !menuEnableButton) && (
                <div className="d-flex justify-content-center">
                  Que pena! Estamos fechados : (
                </div>
              )}
            </div>
          </div>
        </form>
      )}

      <Address
        show={openAddress}
        onClose={() => setOpenAddress(false)}
        distances={distances}
      />
    </App>
  );
};

export default Cart;
